import React, { useCallback, useEffect, useState } from 'react';
import { Routes, Route, NavLink, Link, Navigate } from 'react-router-dom';
import { LogOut, CheckCircle2, AlertCircle, ExternalLink } from 'lucide-react';

import { api } from '../../lib/api.js';
import { useContent } from '../../lib/content.jsx';
import { Loading } from '../../components/states.jsx';
import AdminLogin from './AdminLogin.jsx';
import LecturesAdmin from './LecturesAdmin.jsx';
import AssignmentsAdmin from './AssignmentsAdmin.jsx';
import MaterialsAdmin from './MaterialsAdmin.jsx';
import YearAdmin from './YearAdmin.jsx';
import FilesAdmin from './FilesAdmin.jsx';
import '../../styles/admin.css';

const TABS = [
  { to: 'hodiny', label: 'Hodiny' },
  { to: 'ukoly', label: 'Úkoly' },
  { to: 'materialy', label: 'Materiály' },
  { to: 'rocnik', label: 'Ročník a texty' },
  { to: 'soubory', label: 'Soubory' },
];

/** Krátká potvrzovací hláška ve spodní části obrazovky. */
function Toast({ toast }) {
  if (!toast) return null;
  const failed = toast.tone === 'error';
  return (
    <div className={`toast ${failed ? 'toast-error' : 'toast-ok'}`} role="status">
      {failed ? <AlertCircle size={16} /> : <CheckCircle2 size={16} />}
      <span>{toast.message}</span>
    </div>
  );
}

export default function Admin() {
  const [auth, setAuth] = useState({ status: 'checking', username: null });
  const [years, setYears] = useState([]);
  const [yearId, setYearId] = useState(null);
  const [files, setFiles] = useState([]);
  const [toast, setToast] = useState(null);
  const { reload: reloadPublicContent } = useContent();

  const notify = useCallback((message, tone = 'ok') => {
    setToast({ message, tone });
    setTimeout(() => setToast(null), tone === 'error' ? 5000 : 2500);
  }, []);

  const loadContent = useCallback(async () => {
    const data = await api.admin.content();
    setYears(data.years);
    // po smazání ročníku vybereme jiný; jinak držíme ten zvolený
    setYearId((current) =>
      data.years.some((y) => y.id === current)
        ? current
        : (data.years.find((y) => y.isCurrent) || data.years[0])?.id ?? null);
    reloadPublicContent();
    return data.years;
  }, [reloadPublicContent]);

  const loadFiles = useCallback(async () => {
    try {
      setFiles(await api.admin.files());
    } catch {
      /* seznam souborů není kritický, případnou chybu ohlásí až vlastní stránka */
    }
  }, []);

  useEffect(() => {
    api.auth.me()
      .then((user) => setAuth({ status: 'in', username: user.username }))
      .catch(() => setAuth({ status: 'out', username: null }));
  }, []);

  useEffect(() => {
    if (auth.status !== 'in') return;
    loadContent().catch((error) => notify(error.message, 'error'));
    loadFiles();
  }, [auth.status, loadContent, loadFiles, notify]);

  const logout = async () => {
    await api.auth.logout().catch(() => {});
    setAuth({ status: 'out', username: null });
  };

  if (auth.status === 'checking') return <Loading label="Ověřuji přihlášení…" />;
  if (auth.status === 'out') {
    return <AdminLogin onSuccess={(username) => setAuth({ status: 'in', username })} />;
  }

  const year = years.find((y) => y.id === yearId) || null;

  // sdílený kontext pro všechny stránky správy
  const shared = {
    year, years, files, notify,
    reload: async () => { await loadContent(); },
    reloadFiles: loadFiles,
  };

  return (
    <>
      <header className="admin-header">
        <div className="shell admin-bar">
          <Link to="/admin" className="brand">
            <span><span className="brand-mark">/</span> Správa</span>
          </Link>

          {years.length > 0 && (
            <>
              <label className="sr-only" htmlFor="vyber-rocniku">Ročník</label>
              <select
                id="vyber-rocniku"
                className="select"
                style={{ width: 'auto', minWidth: '8.5rem' }}
                value={yearId ?? ''}
                onChange={(event) => setYearId(Number(event.target.value))}
              >
                {years.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}{option.isCurrent ? ' (aktuální)' : ''}
                  </option>
                ))}
              </select>
            </>
          )}

          <Link to="/" className="btn btn-sm btn-ghost" title="Otevřít web">
            <ExternalLink size={15} /><span className="sr-only">Otevřít web</span>
          </Link>
          <button type="button" className="btn btn-sm btn-ghost" onClick={logout}>
            <LogOut size={15} /><span className="sr-only">Odhlásit</span>
          </button>
        </div>

        <div className="shell">
          <nav className="admin-tabs" aria-label="Sekce správy">
            {TABS.map((tab) => (
              <NavLink key={tab.to} to={`/admin/${tab.to}`} className="admin-tab">{tab.label}</NavLink>
            ))}
          </nav>
        </div>
      </header>

      <main className="shell admin-page">
        {years.length === 0 ? (
          <Loading label="Načítám obsah…" />
        ) : (
          <Routes>
            <Route index element={<Navigate to="hodiny" replace />} />
            <Route path="hodiny" element={<LecturesAdmin {...shared} />} />
            <Route path="ukoly" element={<AssignmentsAdmin {...shared} />} />
            <Route path="materialy" element={<MaterialsAdmin {...shared} />} />
            <Route path="rocnik" element={<YearAdmin {...shared} username={auth.username} />} />
            <Route path="soubory" element={<FilesAdmin {...shared} />} />
            <Route path="*" element={<Navigate to="/admin/hodiny" replace />} />
          </Routes>
        )}
      </main>

      <Toast toast={toast} />
    </>
  );
}
